type Node = Record<string, unknown>;

const ID_KEY = "@id";

function isPlainObject(value: unknown): value is Node {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readId(node: unknown): string | null {
  if (!isPlainObject(node)) return null;

  const id = node[ID_KEY];
  return typeof id === "string" && id.trim() !== "" ? id : null;
}

function isGap(value: unknown): boolean {
  return value === undefined || value === null;
}

function isSameValue(a: unknown, b: unknown): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

function unite(current: unknown[], incoming: unknown[]): unknown[] {
  const united = [...current];

  incoming.forEach((item) => {
    if (!united.some((existing) => isSameValue(existing, item))) {
      united.push(item);
    }
  });

  return united;
}

function mergeInto(target: Node, source: Node): Node {
  const merged: Node = { ...target };

  Object.entries(source).forEach(([key, value]) => {
    const current = merged[key];

    if (Array.isArray(current) && Array.isArray(value)) {
      merged[key] = unite(current, value);
      return;
    }

    if (isGap(current)) merged[key] = value;
  });

  return merged;
}

/**
 * Consolida num nó só os nós do grafo que declaram o mesmo `@id`.
 *
 * Existe para o critério "quando mais de uma fonte emitir a mesma entidade, o
 * resultado deve ser um único nó, sem duplicidade": builders diferentes podem
 * conhecer partes distintas da mesma entidade, e o `@id` é o que diz que são a
 * mesma coisa.
 *
 * As regras da fusão são convenção nossa, não do schema.org:
 * - nó sem `@id` não agrupa com ninguém e passa direto;
 * - o nó consolidado fica na posição da primeira ocorrência;
 * - por propriedade, a primeira definição vence — as seguintes só preenchem
 *   lacunas, de modo que a ordem de `BUILDERS` decide quem tem a palavra final;
 * - arrays são unidos, sem repetir valor igual.
 */
export function mergeById<T>(nodes: readonly T[]): T[] {
  const positions = new Map<string, number>();
  const merged: T[] = [];

  nodes.forEach((node) => {
    const id = readId(node);

    if (id === null) {
      merged.push(node);
      return;
    }

    const position = positions.get(id);

    if (position === undefined) {
      positions.set(id, merged.length);
      merged.push(node);
      return;
    }

    merged[position] = mergeInto(
      merged[position] as Node,
      node as Node,
    ) as unknown as T;
  });

  return merged;
}
